"use client";

import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type ConfirmOptions = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive styling on the confirm button. Defaults to true. */
  destructive?: boolean;
  /**
   * Base z-index for the backdrop; the popup renders one above. Raise this
   * when confirming from inside a surface that already sits above z-1000
   * (story composer, viewers) or the backdrop lands behind it.
   */
  layer?: number;
};

type ConfirmDialogProps = ConfirmOptions & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel?: () => void;
  pending?: boolean;
};

/**
 * Themed replacement for `window.confirm`. The native dialog renders system
 * chrome (and the origin) inside the Capacitor shells, so destructive actions
 * must route through this instead.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = true,
  pending = false,
  layer,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[340px] rounded-[24px] border-border bg-surface p-5 text-text-primary shadow-xl dark:border-white/[0.12] dark:bg-[#141418] dark:text-white"
        layer={layer ?? 1000}
        showCloseButton={false}
      >
        <DialogHeader className="text-left">
          <DialogTitle className="text-text-primary dark:text-white">
            {title}
          </DialogTitle>
          {description ? (
            <DialogDescription className="text-text-secondary dark:text-white/58">
              {description}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogFooter className="gap-2 sm:flex-col-reverse">
          <Button
            type="button"
            className={cn(
              "h-11 rounded-xl font-semibold",
              destructive
                ? "bg-error text-destructive-foreground hover:bg-error/90 dark:bg-white dark:text-black dark:hover:bg-white/90"
                : undefined,
            )}
            disabled={pending}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-11 rounded-xl text-text-secondary hover:bg-surface-alt hover:text-text-primary dark:text-white/72 dark:hover:bg-glass-bg-08 dark:hover:text-white"
            disabled={pending}
            onClick={() => {
              onCancel?.();
              onOpenChange(false);
            }}
          >
            {cancelLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type PendingConfirm = {
  options: ConfirmOptions;
  resolve: (confirmed: boolean) => void;
};

/**
 * Promise-based confirm, shaped so an imperative `if (!confirm(...)) return;`
 * becomes `if (!(await confirm({...}))) return;`.
 *
 * Render the returned `confirmDialog` node somewhere in the component tree.
 */
export function useConfirm() {
  const [pending, setPending] = React.useState<PendingConfirm | null>(null);
  // Keeps the resolver single-shot across dismiss paths (button, esc, backdrop).
  const pendingRef = React.useRef<PendingConfirm | null>(null);
  pendingRef.current = pending;

  const settle = React.useCallback((confirmed: boolean) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(confirmed);
  }, []);

  const confirm = React.useCallback((options: ConfirmOptions) => {
    // A second call while one is open resolves the first as cancelled.
    const previous = pendingRef.current;
    previous?.resolve(false);

    return new Promise<boolean>((resolve) => {
      const next = { options, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  React.useEffect(
    () => () => {
      pendingRef.current?.resolve(false);
      pendingRef.current = null;
    },
    [],
  );

  const confirmDialog = pending ? (
    <ConfirmDialog
      {...pending.options}
      open
      onOpenChange={(next) => {
        if (!next) settle(false);
      }}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  ) : null;

  return { confirm, confirmDialog };
}
